import React, { useState, useEffect } from 'react'
import { API_BASE } from '../utils/api'
import { useCity } from '../context/CityContext'

export interface FilterValues {
    search: string;
    city: string;
    type: string;
    bedrooms: string;
    minPrice: string;
    maxPrice: string;
    subCategory: string;
    furnishing: string;
    minSize: string;
    maxSize: string;
    bathrooms: string;
    buildingAge: string;
    sellType: string;
    facing: string;
    postedBy: string;
    parkingType: string;
    constructionStatus: string;
    reraRegistered: string;
    balcony: string;
    liftAvailable: boolean;
    powerBackup: boolean;
    gatedCommunity: boolean;
    cornerProperty: boolean;
    parkFacing: boolean;
    nearbyMetro: boolean;
    listingType: string;
    projectStatus: string;
    locality: string;
}

interface FilterOptions {
    cities: string[];
    types: string[];
    bedrooms: number[];
}

export default function SideFilter({ show, setShow, filters, onFilterChange }: {
    show: any, setShow: any,
    filters: FilterValues,
    onFilterChange: (filters: FilterValues) => void
}) {
    const { cities, allLocalities } = useCity();
    
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({
        where: true,
        localities: !!filters.city,
        propertyTypes: true,
        subCategories: false,
        bedrooms: false,
        priceRange: false,
        status: false,
        listing: false,
        amenities: false
    })
    
    const [options, setOptions] = useState<FilterOptions>({ cities: [], types: [], bedrooms: [] })

    useEffect(() => {
        fetch(`${API_BASE}/properties/filter-options`)
            .then(r => r.json())
            .then(data => setOptions(data))
            .catch(console.error)
    }, [])

    const toggleSection = (section: string) => {
        setOpenSections(prev => ({ ...prev, [section]: !prev[section] }))
    }

    const handleChange = (key: keyof FilterValues, value: any) => {
        onFilterChange({ ...filters, [key]: value })
    }

    // Property subtype mapping
    const subTypeMap: Record<string, string[]> = {
        'Residential': ['Flat', 'Villa', 'Penthouse', 'Duplex', 'Bungalow', 'Studio'],
        'Commercial': ['Office Space', 'Shop', 'Showroom', 'Warehouse', 'Co-working'],
        'PG/Co-living': ['PG for Boys', 'PG for Girls', 'Co-living Space'],
        'Plot': ['Residential Plot', 'Commercial Plot', 'Industrial Plot'],
        'Land': ['Agricultural Land', 'Industrial Land', 'Farm Land', 'NA Land']
    }

    return (
        <>
            {/* Mobile Overlay */}
            <div 
                className={`fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm transition-all duration-300 lg:hidden ${show ? 'visible opacity-100' : 'invisible opacity-0'}`} 
                onClick={() => setShow(false)}
            />
            
            {/* Sidebar Content */}
            <div className={`
                fixed lg:static left-0 top-0 z-[101] lg:z-0
                h-full lg:h-auto w-80 lg:w-full
                bg-[#f4f7fa] lg:bg-white
                rounded-r-3xl lg:rounded-3xl
                shadow-2xl lg:shadow-sm
                overflow-y-auto lg:overflow-visible
                transition-transform duration-500
                ${show ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `} onClick={e => e.stopPropagation()}>
                
                <div className="p-6 space-y-4">
                    {/* Mobile Header */}
                    <div className="lg:hidden flex items-center justify-between mb-6">
                        <h4 className="text-xl font-bold text-slate-900">Filters</h4>
                        <button onClick={() => setShow(false)} className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>

                    {/* Search Bar */}
                    <div className="relative">
                        <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-[#05144b]"></i>
                        <input 
                            type="text" 
                            className="w-full pl-11 pr-4 py-3.5 bg-[#eff4fb] border-none rounded-xl text-sm font-bold text-slate-700 placeholder-slate-400 focus:ring-2 focus:ring-[#05144b]/20 shadow-inner" 
                            placeholder="Project or Developer..."
                            value={filters.search}
                            onChange={e => handleChange('search', e.target.value)}
                        />
                    </div>

                    {/* Toggles */}
                    <div className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-2.5">
                            <i className="fa-solid fa-shield-check text-emerald-500 text-sm"></i>
                            <span className="text-[12px] font-black text-black">RERA Registered</span>
                        </div>
                        <button 
                            onClick={() => handleChange('reraRegistered', filters.reraRegistered ? '' : 'Yes')}
                            className={`w-10 h-5 rounded-full relative transition-colors ${filters.reraRegistered ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95]' : 'bg-slate-200'}`}
                        >
                            <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-transform ${filters.reraRegistered ? 'translate-x-6' : 'translate-x-1'}`}></div>
                        </button>
                    </div>

                    {/* Accordions */}
                    <div className="space-y-2 pt-2">
                        {/* WHERE - City */}
                        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                            <button onClick={() => toggleSection('where')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                <div className="text-left">
                                    <h4 className="text-[12px] font-black text-black leading-none mb-1.5 uppercase tracking-wider">City</h4>
                                    <span className="text-xs font-bold text-[#05144b]">{filters.city || 'All Cities'}</span>
                                </div>
                                <i className={`fa-solid fa-chevron-${openSections.where ? 'up' : 'down'} text-[10px] text-[#05144b]`}></i>
                            </button>
                            {openSections.where && cities.length > 0 && (
                                <div className="px-5 pb-4 max-h-48 overflow-y-auto custom-scrollbar">
                                    <div 
                                        onClick={() => { handleChange('city', ''); handleChange('locality', ''); }} 
                                        className={`py-2 text-xs font-bold cursor-pointer hover:text-primary transition-colors ${!filters.city ? 'text-primary bg-primary/5 -mx-2 px-2 rounded-lg' : 'text-slate-500'}`}
                                    >
                                        All Cities
                                    </div>
                                    {cities.map(city => (
                                        <div key={city} onClick={() => { handleChange('city', city); handleChange('locality', ''); }} className={`py-2 text-xs font-bold cursor-pointer hover:text-primary transition-colors ${filters.city === city ? 'text-primary bg-primary/5 -mx-2 px-2 rounded-lg' : 'text-slate-500'}`}>
                                            {city}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* LOCALITIES - Dynamic based on City */}
                        {filters.city && (
                            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                                <button onClick={() => toggleSection('localities')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                    <div className="text-left">
                                        <h4 className="text-[12px] font-black text-black leading-none mb-1.5 uppercase tracking-wider">Localities</h4>
                                        <span className="text-xs font-bold text-[#05144b]">{filters.locality || 'All Areas'}</span>
                                    </div>
                                    <i className={`fa-solid fa-chevron-${openSections.localities ? 'up' : 'down'} text-[10px] text-[#05144b]`}></i>
                                </button>
                                {openSections.localities && (
                                    <div className="px-5 pb-4 max-h-48 overflow-y-auto custom-scrollbar">
                                        <div 
                                            onClick={() => handleChange('locality', '')} 
                                            className={`py-2 text-xs font-bold cursor-pointer hover:text-primary transition-colors ${!filters.locality ? 'text-primary bg-primary/5 -mx-2 px-2 rounded-lg' : 'text-slate-500'}`}
                                        >
                                            All Areas
                                        </div>
                                        {allLocalities.filter(l => l.cityName === filters.city).map(loc => (
                                            <div key={loc._id} onClick={() => handleChange('locality', loc.name)} className={`py-2 text-xs font-bold cursor-pointer hover:text-primary transition-colors ${filters.locality === loc.name ? 'text-primary bg-primary/5 -mx-2 px-2 rounded-lg' : 'text-slate-500'}`}>
                                                {loc.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* LISTING TYPE */}
                        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                            <button onClick={() => toggleSection('listing')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                <div className="text-left">
                                    <h4 className="text-[12px] font-black text-black leading-none mb-1.5 uppercase tracking-wider">Listing Type</h4>
                                    <span className="text-xs font-bold text-[#05144b]">{filters.listingType || 'Sale & Rent'}</span>
                                </div>
                                <i className={`fa-solid fa-chevron-${openSections.listing ? 'up' : 'down'} text-[10px] text-[#05144b]`}></i>
                            </button>
                            {openSections.listing && (
                                <div className="px-5 pb-4 flex flex-wrap gap-2">
                                    {['For Sale', 'For Rent'].map(type => (
                                        <button 
                                            key={type} 
                                            onClick={() => handleChange('listingType', filters.listingType === type ? '' : type)}
                                            className={`px-4 py-2 text-[11px] font-bold rounded-lg border transition-all ${filters.listingType === type ? 'bg-[#05144b] text-white border-[#05144b]' : 'bg-white text-slate-500 border-slate-100 hover:border-primary'}`}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* PROPERTY TYPES */}
                        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                            <button onClick={() => toggleSection('propertyTypes')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                <div className="text-left">
                                    <h4 className="text-[12px] font-black text-black leading-none mb-1.5 uppercase tracking-wider">Property Types</h4>
                                    <span className="text-xs font-bold text-[#05144b]">{filters.type || 'All Types'}</span>
                                </div>
                                <i className={`fa-solid fa-chevron-${openSections.propertyTypes ? 'up' : 'down'} text-[10px] text-[#05144b]`}></i>
                            </button>
                            {openSections.propertyTypes && options.types.length > 0 && (
                                <div className="px-5 pb-4 max-h-48 overflow-y-auto custom-scrollbar">
                                    <div 
                                        onClick={() => { handleChange('type', ''); handleChange('subCategory', ''); }} 
                                        className={`py-2 text-xs font-bold cursor-pointer hover:text-primary transition-colors ${!filters.type ? 'text-primary bg-primary/5 -mx-2 px-2 rounded-lg' : 'text-slate-500'}`}
                                    >
                                        All Types
                                    </div>
                                    {options.types.map(type => (
                                        <div key={type} onClick={() => { handleChange('type', type); handleChange('subCategory', ''); }} className={`py-2 text-xs font-bold cursor-pointer hover:text-primary transition-colors ${filters.type === type ? 'text-primary bg-primary/5 -mx-2 px-2 rounded-lg' : 'text-slate-500'}`}>
                                            {type}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* SUB CATEGORIES - Dynamic based on Type */}
                        {filters.type && subTypeMap[filters.type] && (
                            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                                <button onClick={() => toggleSection('subCategories')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                    <div className="text-left">
                                        <h4 className="text-[12px] font-black text-black leading-none mb-1.5 uppercase tracking-wider">Specific Type</h4>
                                        <span className="text-xs font-bold text-[#05144b]">{filters.subCategory || 'Select Subtype'}</span>
                                    </div>
                                    <i className={`fa-solid fa-chevron-${openSections.subCategories ? 'up' : 'down'} text-[10px] text-[#05144b]`}></i>
                                </button>
                                {openSections.subCategories && (
                                    <div className="px-5 pb-4 flex flex-wrap gap-2">
                                        {subTypeMap[filters.type].map(sub => (
                                            <button 
                                                key={sub} 
                                                onClick={() => handleChange('subCategory', filters.subCategory === sub ? '' : sub)}
                                                className={`px-3 py-1.5 text-[11px] font-bold rounded-lg border transition-all ${filters.subCategory === sub ? 'bg-[#05144b] text-white border-[#05144b]' : 'bg-white text-slate-500 border-slate-100 hover:border-primary'}`}
                                            >
                                                {sub}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* BEDROOMS */}
                        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                            <button onClick={() => toggleSection('bedrooms')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                <div className="text-left">
                                    <h4 className="text-[12px] font-black text-black leading-none mb-1.5 uppercase tracking-wider">Bedrooms</h4>
                                    <span className="text-xs font-bold text-[#05144b]">{filters.bedrooms ? `${filters.bedrooms} BHK` : 'Any BHK'}</span>
                                </div>
                                <i className={`fa-solid fa-chevron-${openSections.bedrooms ? 'up' : 'down'} text-[10px] text-[#05144b]`}></i>
                            </button>
                            {openSections.bedrooms && (
                                <div className="px-5 pb-4 grid grid-cols-4 gap-2">
                                    {[1, 2, 3, 4, 5].map(bed => (
                                        <button 
                                            key={bed} 
                                            onClick={() => handleChange('bedrooms', String(bed))} 
                                            className={`py-2 text-xs font-black rounded-lg border transition-all ${filters.bedrooms === String(bed) ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] border-[#05144b] text-white shadow-md' : 'bg-slate-50 border-slate-100 text-slate-500 hover:border-primary hover:text-primary'}`}
                                        >
                                            {bed}
                                        </button>
                                    ))}
                                    <button 
                                        onClick={() => handleChange('bedrooms', '')} 
                                        className={`col-span-4 py-2 text-[11px] font-bold transition-colors ${!filters.bedrooms ? 'text-primary' : 'text-slate-400 hover:text-primary'}`}
                                    >
                                        Clear Selection
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* PROJECT STATUS */}
                        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                            <button onClick={() => toggleSection('status')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                <div className="text-left">
                                    <h4 className="text-[12px] font-black text-black leading-none mb-1.5 uppercase tracking-wider">Project Status</h4>
                                    <span className="text-xs font-bold text-[#05144b]">{filters.projectStatus || 'Any Status'}</span>
                                </div>
                                <i className={`fa-solid fa-chevron-${openSections.status ? 'up' : 'down'} text-[10px] text-[#05144b]`}></i>
                            </button>
                            {openSections.status && (
                                <div className="px-5 pb-4 space-y-1.5">
                                    {['New Launch', 'Under Construction', 'Ready to Move'].map(status => (
                                        <div 
                                            key={status} 
                                            onClick={() => handleChange('projectStatus', filters.projectStatus === status ? '' : status)}
                                            className={`py-2 text-xs font-bold cursor-pointer hover:text-primary transition-colors ${filters.projectStatus === status ? 'text-primary bg-primary/5 -mx-2 px-2 rounded-lg' : 'text-slate-500'}`}
                                        >
                                            {status}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* PRICE RANGE */}
                        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                            <button onClick={() => toggleSection('priceRange')} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                <div className="text-left">
                                    <h4 className="text-[12px] font-black text-black leading-none mb-1.5 uppercase tracking-wider">Price Range</h4>
                                    <span className="text-xs font-bold text-[#05144b]">
                                        {filters.minPrice || filters.maxPrice ? `${filters.minPrice || '0'} - ${filters.maxPrice || 'Any'}` : 'Min - Max Price'}
                                    </span>
                                </div>
                                <i className={`fa-solid fa-chevron-${openSections.priceRange ? 'up' : 'down'} text-[10px] text-[#05144b]`}></i>
                            </button>
                            {openSections.priceRange && (
                                <div className="px-5 pb-5 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-bold text-slate-400 uppercase">Min Price</label>
                                            <input 
                                                type="number" 
                                                placeholder="0"
                                                className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary"
                                                value={filters.minPrice}
                                                onChange={e => handleChange('minPrice', e.target.value)}
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-bold text-slate-400 uppercase">Max Price</label>
                                            <input 
                                                type="number" 
                                                placeholder="Any"
                                                className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary"
                                                value={filters.maxPrice}
                                                onChange={e => handleChange('maxPrice', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>

                    <button 
                        onClick={() => setShow(false)}
                        className="w-full py-4 mt-6 bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white rounded-xl font-bold text-[13px] transition-all shadow-lg active:scale-95 uppercase tracking-[0.1em]"
                    >
                        Apply Filters
                    </button>
                </div>
            </div>
        </>
    )
}
